<template>
  <div :key="rerenderKey">
    <div v-if="!item.documents || item.documents.length === 0" class="text-center py-6 text-gray-500">
      <div class="text-4xl mb-1">📄</div>
      <p class="text-xs">No documents available</p>
    </div>
    
    <div v-else class="space-y-3">
      <!-- Horizontal scrolling container for multiple documents -->
      <div>
        <div class="flex gap-2 min-w-max">
          <div 
            v-for="(doc, index) in item.documents" 
            :key="'d-' + index"
            class="flex-shrink-0 w-72 border overflow-hidden bg-white"
            style="width:348px;"
          >
            <!-- Compact header: jobFamily, reference, status in one line -->
            <div class="border-b bg-gray-50" style="background-color:#E6F1FB; padding: 4px 12px 0px 8px">
              <!-- Job Family row - full width -->
              <div class="w-full">
                <span class="text-sm font-medium text-gray-700 truncate" :title="doc.jobFamily">{{ doc.jobFamily }}</span>
              </div>
              
              <!-- Reference/Status/Icon row - horizontal layout -->
              <div class="flex items-center justify-between" style="min-height: 36px;">
                <div class="flex items-center gap-1 w-2/3">
                  <!-- Pending changes indicator for this specific document -->
                  <div v-if="hasDocumentPendingChanges(doc)" class="pending-changes-indicator" title="Has pending changes">
                    <v-icon size="12" color="warning">mdi-circle-edit-outline</v-icon>
                  </div>
                  
                  <span class="text-xs text-gray-500 truncate doc-header-ellipsis" :title="`${doc.jobFamily} (${doc.reference})`">{{ doc.reference }}</span>
                </div>
                <v-chip
                  class=""
                  label small
                  outlined
                  :color="doc.lifecycle_status_colour"
                  style="margin-right:10px"
                  :class="['text-xs px-1 py-0.5 rounded flex-shrink-0 doc-header-ellipsis status-chip', doc.lifecycle_status_colour]"
                  >{{ doc.lifecycleStatus }}</v-chip>
                <v-icon title="Preview Document" @click="viewDoc($event, doc.doc_id)">mdi-file-eye-outline</v-icon>
              </div>
            </div>
            
            <!-- Skill summary row underneath document details -->
            <div class="px-2 pb-2 bg-gray-50 flex gap-1" style="background-color:#E6F1FB">
              <div 
                v-for="([rating, count], index) in getDocumentSkillSummary(doc.tags)" 
                :key="'rt' + rating + '-' + index"
                :class="['rating-counter-compact', getSkillSummaryClass(doc.tags, rating)]"
                @click="showProficiencyModal(doc, rating, index)"
                :title="`Click to change ${count} skill${count !== 1 ? 's' : ''} from ${rating}`"
              >
                <!-- Small rounded colored square -->
                <div 
                  class="rating-color-dot"
                  :style="{ backgroundColor: getRatingColorByLevel(rating) }"
                ></div>
                
                <!-- Count and rating text -->
                <span class="compact-text">{{ count }} {{ rating }}</span>
                
                <!-- Chevron down icon -->
                <v-icon size="10" class="compact-chevron">mdi-chevron-down</v-icon>
              </div>
            </div>
            
            <!-- Skills for this specific document -->
            <div style="min-height: calc(-420px + 100vh); max-height: calc(-420px + 100vh); overflow: scroll;">
              <div v-if="doc.tags && doc.tags.length > 0" class="space-y-2">
                
                <!-- Missing focused skills indicator: red box with warning icon -->
                <div v-if="focusedSkills && focusedSkills.length > 0" class="flex flex-col mb-1" :class="[ getMissingFocusedSkills(doc).length > 0 ? 'mt-2' : '']"  style="flex-direction:column; padding-left:2px">
                  <div 
                    v-for="(skill, index) in getMissingFocusedSkills(doc)" 
                    :key="'missing-' + skill + '-' + index"
                    class="missing-skill-indicator flex items-center px-1.5 py-0.5 rounded bg-red-100 border border-red-400 text-red-700 font-semibold"
                    style="font-size: 13px;"
                  >
                    <span class="warning-icon" style="font-size:12px;color:#b91c1c;font-weight:bold; padding-right: 4px;">!</span>
                    <span class="flex-1">{{ skill }}</span>
                    <button 
                      @click="addSkillToDocument(doc, skill)"
                      class="add-skill-btn ml-1 p-0.5 rounded-full bg-green-100 hover:bg-green-200 text-green-700 hover:text-green-800 transition-colors"
                      title="Add skill to document"
                    >
                      <v-icon size="10">mdi-plus</v-icon>
                    </button>
                  </div>
                </div>
                
                <!-- Individual skills: vertical list ordered by sequence with drag handles -->
                <div class="skills-list">
                  <div 
                    v-for="(tag, tagIndex) in getSortedSkills(doc.tags)" 
                    :key="tag.tag_value_id + '-' + tagIndex"
                    :class="['skill-item flex items-center px-1.5 py-1  text-xs border', 
                      getSkillItemClass(tag),
                      { 
                        'drop-zone-active': draggedIndex !== null && draggedIndex !== tagIndex && dropIndicatorIndex === tagIndex && draggedDocIndex === index,
                        'skill-deleted': tag.isDeleted
                      }
                    ]"
                    :draggable="!tag.isDeleted"
                    @dragstart="handleDragStart($event, tag, tagIndex, index)"
                    @dragover="handleDragOver($event, tagIndex, index)"
                    @dragenter="handleDragEnter($event, tagIndex, index)"
                    @dragleave="handleDragLeave($event, tagIndex, index)"
                    @drop="handleDrop($event, tagIndex, index)"
                    @dragend="handleDragEnd($event)"
                  >
                    <!-- Drop indicator line -->
                    <div 
                      v-if="draggedIndex !== null && draggedIndex !== tagIndex && dropIndicatorIndex === tagIndex && draggedDocIndex === index"
                      class="drop-indicator"
                    ></div>
                    
                    <!-- Priority number -->
                    <div class="priority-number flex items-center justify-center text-xs font-bold flex-shrink-0" style="font-size: 14px; min-width: 12px; margin-right: 2px; color: #AAAAAA">
                      {{ tagIndex + 1 }}
                    </div>
                    
                    <!-- Drag handle -->
                    <div class="drag-handle mr-1 text-gray-400 hover:text-gray-600" style="padding: 2px;">
                      <v-icon size="18">mdi-drag</v-icon>
                    </div>
                    
                    <!-- Skill name and rating -->
                    <div class="flex items-center flex-1 min-w-0">
                      <span class="font-semibold leading-tight mr-2 skill-name flex-1" style="font-size: 12px; min-width: 0;"
                        @click.stop="toggleSkillFocus(tag.value)"
                        :title="isSkillFocused(tag.value) ? 'Unfocus skill' : 'Focus skill'"
                        :style="{ cursor: 'pointer', textDecoration: isSkillFocused(tag.value) ? 'underline' : 'none' }"
                      >{{ tag.value }}</span>
                      
                      <!-- Interactive proficiency selector or undo button for deleted -->
                      <div class="proficiency-selector flex-shrink-0" @click.stop>
                        <template v-if="tag.isDeleted">
                          <v-btn
                            small
                            color="success"
                            class="ml-2"
                            style="min-width: 0; padding: 0 8px; font-size: 14px;"
                            @click="undoDeleteSkill(doc, tag, index)"
                            title="Undo delete"
                          >
                            <v-icon left small>mdi-undo</v-icon>
                            Undo
                          </v-btn>
                        </template>
                        <template v-else>
                          <div class="proficiency-selector-container">
                            <div class="rating-segments-container">
                              <div class="rating-segments" @mouseleave="clearHover(tag.value, index)">
                                <div
                                  v-for="(level, levelIndex) in getFilteredProficiencyLevels()"
                                  :key="level + '-' + levelIndex"
                                  :class="[
                                    'rating-segment',
                                    { 
                                      'active': tag.name === level,
                                      'first': levelIndex === 0,
                                      'last': levelIndex === getFilteredProficiencyLevels().length - 1,
                                      'filled': shouldShowFilled(levelIndex, level, tag, index)
                                    }
                                  ]"
                                  :data-level="level"
                                  :data-index="levelIndex"
                                  :style="getSegmentStyle(levelIndex, level, tag, index)"
                                  @click="handleProficiencyChange(doc, tag, level, index)"
                                  @mouseenter="setHoverLevel(levelIndex, level, tag.value, index)"
                                  :title="level"
                                >
                                </div>
                              </div>
                              <div class="proficiency-text" :class="{ unrated: getDisplayProficiencyText(tag, index) === 'Set Proficiency' }">
                                {{ getDisplayProficiencyText(tag, index) }}
                              </div>
                            </div>
                            <button
                              class="delete-skill-btn"
                              @click="handleProficiencyChange(doc, tag, 'DELETE_SKILL', index)"
                              title="Delete Skill"
                            >
                              <v-icon size="18" style="bottom:2px">mdi-trash-can-outline</v-icon>
                            </button>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else class="text-center py-4 text-gray-500 text-xs space-y-2" style="padding-left:2px">
                No specific skills defined
                
                <!-- Also show missing focused skills if any -->
                <div v-if="focusedSkills && focusedSkills.length > 0" class="flex flex-col mt-2 justify-center"   style="flex-direction:column; padding-left:2px">
                  <div 
                    v-for="(skill, index) in getMissingFocusedSkills(doc)" 
                    :key="'missing-empty-' + skill + '-' + index"
                    class="missing-skill-indicator flex items-center px-1.5 py-0.5 rounded bg-red-100 border border-red-400 text-red-700 font-semibold"
                    style="font-size: 13px;"
                  >

                    <span class="flex-1">{{ skill }}</span>
                    <button 
                      @click="addSkillToDocument(doc, skill)"
                      class="add-skill-btn ml-1 p-0.5 rounded-full bg-green-100 hover:bg-green-200 text-green-700 hover:text-green-800 transition-colors"
                      title="Add skill to document"
                    >
                      <v-icon size="10">mdi-plus</v-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Proficiency Change Modal -->
    <v-dialog v-model="proficiencyModalOpen" max-width="380px">
      <v-card>
        <v-card-title class="text-lg">
          Change Proficiency Level
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">
              Change {{ selectedSkillsForProficiencyChange.length }} skill{{ selectedSkillsForProficiencyChange.length !== 1 ? 's' : '' }} 
              from <strong>{{ currentProficiencyLevel }}</strong> to:
            </p>
            <div class="mb-3">
              <div class="text-xs text-gray-500 mb-1">Skills to change:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="(skill, index) in selectedSkillsForProficiencyChange" 
                  :key="skill.tag_value_id + '-' + index"
                  class="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs"
                >
                  {{ skill.value }}
                </span>
              </div>
            </div>
          </div>
          
          <v-select
            v-model="newProficiencyLevel"
            :items="availableProficiencyLevels.filter(level => level !== currentProficiencyLevel)"
            label="New Proficiency Level"
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <div class="proficiency-select-option">
                <!-- Rating segments progression like the main view -->
                <div class="proficiency-select-segments">
                  <div
                    v-for="(level, levelIndex) in getFilteredProficiencyLevels()"
                    :key="level + '-' + levelIndex"
                    :class="[
                      'proficiency-select-segment',
                      { 
                        'first': levelIndex === 0,
                        'last': levelIndex === getFilteredProficiencyLevels().length - 1,
                        'filled': levelIndex <= getFilteredProficiencyLevels().indexOf(item)
                      }
                    ]"
                    :style="levelIndex <= getFilteredProficiencyLevels().indexOf(item) ? { backgroundColor: getRatingColorByLevel(item) } : {}"
                  ></div>
                </div>
                <!-- Text -->
                <span class="proficiency-select-text">{{ item }}</span>
              </div>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="proficiencyModalOpen = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="applyBulkProficiencyChange"
            :disabled="!newProficiencyLevel"
          >
            Apply Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'DocumentDetails',
  props: {
    item: {
      type: Object,
      required: true
    },
    focusedSkills: {
      type: Array,
      default: () => []
    },
    globalFilters: {
      type: Object,
      default: () => ({
        nameSearch: '',
        selectedTypes: [],
        selectedProficiencyLevels: []
      })
    },
    hasDocumentPendingChangesByKey: {
      type: Function,
      required: true
    },
    tags: {
      type: Array,
      default: () => []
    },
    availableProficiencyLevels: {
      type: Array,
      default: () => ['Unrated']
    },
    rerenderKeyVal: {
      type: Number
    }
  },
  emits: ['document-skill-update', 'viewDoc', 'toggleSkillFocus'],
  data() {
    return {
      draggedItem: null,
      draggedIndex: null,
      draggedDocIndex: null,
      dropIndicatorIndex: null,
      isDragging: false,
      draggedElement: null,
      // Proficiency change modal data
      proficiencyModalOpen: false,
      currentProficiencyLevel: '',
      newProficiencyLevel: '',
      selectedSkillsForProficiencyChange: [],
      // Hover state for rating segments (skill-specific)
      hoverStates: {},
      selectedDocumentIndex: null,
      // Cache for expensive computations
      cachedFocusedSkillsSet: new Set(),
      lastFocusedSkillsString: '',
      rerenderKey: 0, // Add a rerender key for forcing v-select reactivity
    }
  },
  computed: {
    // Clean computed property without side effects
    focusedSkillsSet() {
      return this.cachedFocusedSkillsSet
    }
  },
  watch: {
    // Watch for changes to focusedSkills and update cache
    focusedSkills: {
      handler(newSkills) {
        const currentSkillsString = JSON.stringify(newSkills || [])
        if (currentSkillsString !== this.lastFocusedSkillsString) {
          this.cachedFocusedSkillsSet = new Set(newSkills || [])
          this.lastFocusedSkillsString = currentSkillsString
        }
      },
      immediate: true,
      deep: true
    },
    rerenderKeyVal: {
      handler(val) {
        this.triggerRerender();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    viewDoc(ev, id){
      this.$emit("viewDoc", ev, id)
    },
    // Heavily optimized skill item class computation
    getSkillItemClass(tag) {
      if (!tag) return ''
      
      // If skill is deleted, add deleted class
      if (tag.isDeleted) {
        return 'skill-deleted'
      }
      
      // Use cached focused skills set - check focus state directly from props
      const isFocused = this.focusedSkills && this.focusedSkills.includes(tag.value)
      const hasAnyFocusedSkills = this.focusedSkills && this.focusedSkills.length > 0
      
      // Quick name search check first (most common filter)
      if (this.globalFilters?.nameSearch) {
        const searchTerm = this.globalFilters.nameSearch.toLowerCase()
        if (!tag.value.toLowerCase().includes(searchTerm)) {
          return 'skill-dulled'
        }
      }
      
      // Quick focus check
      if (hasAnyFocusedSkills && !isFocused) {
        return 'skill-dulled'
      }
      
      // Only do expensive filter checking if needed
      if (this.globalFilters?.selectedTypes?.length > 0 || this.globalFilters?.selectedProficiencyLevels?.length > 0) {
        if (this.isSkillDulledByFilters(tag)) {
          return 'skill-dulled'
        }
      }
      
      return ''
    },

    // Optimized filter checking with early returns
    isSkillDulledByFilters(tag) {
      if (!this.globalFilters || !tag) return false
      
      // Quick type filter check
      if (this.globalFilters.selectedTypes?.length > 0) {
        const skillTypes = tag.classifiers?.filter(c => c.value)?.map(c => c.value) || []
        if (skillTypes.length > 0) {
          const hasMatchingType = skillTypes.some(type => 
            this.globalFilters.selectedTypes.includes(type)
          )
          if (!hasMatchingType) return true
        }
      }
      
      // Quick proficiency filter check
      if (this.globalFilters.selectedProficiencyLevels?.length > 0) {
        const skillRating = tag.name
        if (skillRating && !this.globalFilters.selectedProficiencyLevels.includes(skillRating)) {
          return true
        }
        if (!skillRating) return true
      }
      
      return false
    },

    // Optimized missing focused skills computation
    getMissingFocusedSkills(doc) {
      if (!this.focusedSkills || this.focusedSkills.length === 0 || !doc) return []
      
      // Use Set for O(1) lookup
      const docSkillValuesSet = new Set((doc.tags || []).map(tag => tag.value))
      
      // Filter with early return for name search
      return this.focusedSkills.filter(skill => {
        // Quick document check first
        if (docSkillValuesSet.has(skill)) return false
        
        // Only do name search if needed
        if (this.globalFilters?.nameSearch) {
          const searchTerm = this.globalFilters.nameSearch.toLowerCase()
          return skill.toLowerCase().includes(searchTerm)
        }
        return true
      })
    },

    // Simplified document skill summary
    getDocumentSkillSummary(tags) {
      if (!tags) return []
      
      const summary = {}
      tags.forEach(tag => {
        const rating = tag.name || 'Unrated'
        summary[rating] = (summary[rating] || 0) + 1
      })

      // Sort by proficiency priority using availableProficiencyLevels order
      const order = [...this.availableProficiencyLevels, 'Unrated']
      return Object.entries(summary).sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    },

    // Simplified skill summary class
    getSkillSummaryClass(tags, rating) {
      if (!tags || !rating) return ''
      
      // Quick focus check
      const hasAnyFocusedSkills = this.focusedSkills.length > 0
      if (hasAnyFocusedSkills) {
        const hasRatingInFocus = tags.some(tag => 
          (tag.name || 'Unrated') === rating && this.focusedSkillsSet.has(tag.value)
        )
        if (!hasRatingInFocus) return 'skill-dulled'
      }
      
      // Quick filter check
      if (this.globalFilters?.selectedTypes?.length > 0 || this.globalFilters?.selectedProficiencyLevels?.length > 0) {
        const ratingSkills = tags.filter(tag => (tag.name || 'Unrated') === rating)
        const isRatingDulledByFilters = ratingSkills.some(tag => this.isSkillDulledByFilters(tag))
        if (isRatingDulledByFilters) return 'skill-dulled'
      }
      
      return ''
    },

    // Optimized rating focus check
    hasRatingInFocus(tags, rating) {
      if (!this.focusedSkills || this.focusedSkills.length === 0) return true
      if (!tags || !rating) return false
      
      return tags.some(tag => 
        (tag.name || 'Unrated') === rating && this.focusedSkillsSet.has(tag.value)
      )
    },

    // Simplified drag operations
    handleDragStart(event, skill, skillIndex, docIndex) {
      // Prevent event from bubbling to parent card's drag handler
      event.stopPropagation()
      
      this.draggedSkill = skill
      this.draggedIndex = skillIndex
      this.draggedDocIndex = docIndex
      this.isDragging = true
      this.draggedElement = event.target // Store reference to dragged element
      event.dataTransfer.effectAllowed = 'move'
      event.target.style.opacity = '0.5'
    },

    // Drag and drop handlers for skill reordering
    handleDragOver(event, index) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        this.dropIndicatorIndex = index
      }
    },

    handleDragEnter(event, index) {
      event.preventDefault()
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        this.dropIndicatorIndex = index
      }
    },

    handleDragLeave(event, index) {
      // Only clear if we're leaving the drop zone completely
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX
      const y = event.clientY
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        if (this.dropIndicatorIndex === index) {
          this.dropIndicatorIndex = null
        }
      }
    },

    handleDragEnd(event) {
      // Prevent event from bubbling to parent card's drag handler
      event.stopPropagation()
      
      // Remove the inline opacity style from the stored dragged element
      if (this.draggedElement) {
        this.draggedElement.style.removeProperty('opacity')
        this.draggedElement = null
      }
      this.draggedSkill = null
      this.draggedIndex = null
      this.draggedDocIndex = null
      this.dropIndicatorIndex = null
      // Reset dragging flag after a short delay to prevent accidental clicks
      setTimeout(() => {
        this.isDragging = false
      }, 100)
    },

    // Optimized proficiency change method
    changeProficiency(doc, tag, newProficiency, docIndex) {
      // Emit change event BEFORE making modifications
      this.$emit('document-skill-update', {
        documentName: doc.jobFamily || doc.name,
        documentReference: doc.reference || '',
        doc_id: doc.doc_id,
        dp_id: doc.dp_id,
        dpa_id: doc.dpa_id
      })
      
      // Find the specific tag by its unique identifier (tag_value_id) and doc_id
      const targetTag = this.item.documents[docIndex].tags.find(t => 
        t.tag_value_id === tag.tag_value_id && 
        t.value === tag.value
      )
      
      if (targetTag) {
        // If the skill was previously deleted and we're setting a new proficiency, restore it
        if (targetTag.isDeleted && newProficiency !== 'DELETE_SKILL') {
          targetTag.isDeleted = false
          // Always restore to previous proficiency if available and not DELETE_SKILL/null
          if (targetTag._previousName && targetTag._previousName !== 'DELETE_SKILL' && targetTag._previousName !== null) {
            targetTag.name = targetTag._previousName
            targetTag.tag_type_rating_id = this.getRatingIdForProficiency(targetTag._previousName, targetTag)
          } else if (newProficiency === 'Unrated') {
            targetTag.name = null
            targetTag.tag_type_rating_id = null
          } else if (newProficiency !== 'DELETE_SKILL') {
            targetTag.name = newProficiency
            targetTag.tag_type_rating_id = this.getRatingIdForProficiency(newProficiency, targetTag)
          }
          // Always clear _previousName after restoration
          delete targetTag._previousName
          // Reorder skills after restoration
          this.reorderSkillsAfterRestoration(docIndex, targetTag)
        } else {
          // Normal proficiency change (not restoring from deleted)
          if (newProficiency === 'Unrated') {
            targetTag.name = null
            targetTag.tag_type_rating_id = null
          } else if (newProficiency !== 'DELETE_SKILL') {
            targetTag.name = newProficiency
            targetTag.tag_type_rating_id = this.getRatingIdForProficiency(newProficiency, targetTag)
          }
        }

        // If the tag name is still DELETE_SKILL for any reason, forcibly clear it
        if (targetTag.name === 'DELETE_SKILL') {
          targetTag.name = null
          targetTag.tag_type_rating_id = null
        }

        // Force Vue to update the v-select by replacing the tag object (reactivity edge case)
        this.$set(this.item.documents[docIndex].tags, this.item.documents[docIndex].tags.indexOf(targetTag), { ...targetTag })

        // Emit notification for user feedback
        const action = targetTag.isDeleted ? 'restored and updated' : 'updated'
        this.$emit('notification', {
          text: `Proficiency ${action} to ${newProficiency} for ${tag.value}`,
          type: 'success'
        })
      }
    },

    // New method to reorder skills after restoration
    reorderSkillsAfterRestoration(docIndex, restoredTag) {
      const allTags = this.item.documents[docIndex].tags
      const activeTags = allTags.filter(tag => !tag.isDeleted && tag !== restoredTag)
      const deletedTags = allTags.filter(tag => tag.isDeleted)

      // Find the best position for the restored skill (try to maintain original sequence if possible)
      const originalSequence = restoredTag._originalSequence !== undefined
        ? restoredTag._originalSequence
        : restoredTag.sequence

      if (restoredTag._originalSequence === undefined) {
        restoredTag._originalSequence = originalSequence
      }

      // Calculate insert position, but if skills were reordered after deletion,
      // the correct index is one lower (unless originalSequence is 1)
      let insertPosition = activeTags.length
      for (let i = 0; i < activeTags.length; i++) {
        if (activeTags[i].sequence >= originalSequence) {
          insertPosition = i
          break
        }
      }


      activeTags.splice(insertPosition, 0, restoredTag)

      // Reorder active tags with consecutive sequence numbers
      activeTags.forEach((tag, index) => {
        tag.sequence = index + 1
      })

      // Set deleted tags' sequence to null
      deletedTags.forEach(tag => {
        tag.sequence = null
      })
    },

    applyBulkProficiencyChange() {
      if (!this.newProficiencyLevel || this.selectedSkillsForProficiencyChange.length === 0) {
        return
      }

      const doc = this.item.documents[this.selectedDocumentIndex]
      
      // Emit change event BEFORE making any modifications
      this.$emit('document-skill-update', {
        documentName: doc.jobFamily || doc.name,
        documentReference: doc.reference || '',
        doc_id: doc.doc_id,
        dp_id: doc.dp_id,
        dpa_id: doc.dpa_id
      })

      // Get the rating ID for the new proficiency level
      const ratingId = this.getRatingIdForProficiency(this.newProficiencyLevel)

      // Update each selected skill individually by its unique identifier
      this.selectedSkillsForProficiencyChange.forEach(skillToChange => {
        const tagToUpdate = this.item.documents[this.selectedDocumentIndex].tags.find(
          t => t.tag_value_id === skillToChange.tag_value_id && 
               t.value === skillToChange.value
        )
        
        if (tagToUpdate) {
          // Handle 'Unrated' as null/empty
          if (this.newProficiencyLevel === 'Unrated') {
            tagToUpdate.name = null
            tagToUpdate.tag_type_rating_id = null
          } else {
            tagToUpdate.name = this.newProficiencyLevel
            tagToUpdate.tag_type_rating_id = ratingId
          }
        }
      })
      // Emit notification for bulk change
      this.$emit('notification', {
        text: `Updated ${this.selectedSkillsForProficiencyChange.length} skills to ${this.newProficiencyLevel}`,
        type: 'success'
      })

      // Close modal
      this.proficiencyModalOpen = false
    },

    // Helper method to get rating ID for a proficiency level
    getRatingIdForProficiency(proficiencyName) {
      if (!proficiencyName || proficiencyName === 'Unrated') {
        return null
      }

      // Find the rating ID from the tags prop data
      // Look for any tag type that has ratings, then find the matching rating
      for (const tagType of this.tags) {
        if (tagType.ratings && tagType.ratings.length > 0) {
          const rating = tagType.ratings.find(r => r.name === proficiencyName)
          if (rating) {
            return rating.tag_type_rating_id
          }
        }
      }

      // Fallback to basic mapping if not found in tags data
      const ratingMap = {
        'Basic': 1,
        'Intermediate': 2, 
        'Advanced': 3,
        'Expert': 4,
        'Strategic': 5
      }

      return ratingMap[proficiencyName] || null
    },

    getRatingColor(ratingName) {
      const colors = {
        'Basic': 'bg-gray-100 text-gray-800 border-gray-300',
        'Foundational': 'bg-gray-100 text-gray-800 border-gray-300',
        'Intermediate': 'bg-lime-100 text-lime-800 border-lime-300',
        'Developing': 'bg-lime-100 text-lime-800 border-lime-300',
        'Proficient': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Advanced': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'Expert': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Strategic': 'bg-orange-100 text-orange-800 border-orange-300'
      }
      return colors[ratingName] || 'bg-gray-100 text-gray-800 border-gray-300'
    },

    getShortRatingName(ratingName) {
      const abbreviations = {
        'Unrated': 'UN',
        'Basic': 'B',
        'Intermediate': 'I',
        'Advanced': 'A',
        'Expert': 'E',
        'Strategic': 'S'
      }
      return abbreviations[ratingName] || ratingName.charAt(0)
    },

    setHoverLevel(levelIndex, level, skillName, docIndex) {
      const key = `${docIndex}-${skillName}`
      this.$set(this.hoverStates, key, {
        levelIndex: levelIndex,
        level: level
      })
    },

    clearHover(skillName, docIndex) {
      const key = `${docIndex}-${skillName}`
      this.$delete(this.hoverStates, key)
    },

    shouldShowFilled(currentIndex, currentLevel, tag, docIndex) {
      const key = `${docIndex}-${tag.value}`
      const hoverState = this.hoverStates[key]
      const filteredLevels = this.getFilteredProficiencyLevels()
      
      // If hovering on this specific skill, show fill up to hover level
      if (hoverState) {
        return currentIndex <= hoverState.levelIndex
      }
      
      // If not hovering, show fill up to current rating
      if (tag.name && tag.name !== 'Unrated') {
        const currentRatingIndex = filteredLevels.indexOf(tag.name)
        return currentRatingIndex >= 0 && currentIndex <= currentRatingIndex
      }
      
      // If no rating is set, don't fill any segments
      return false
    },

    getSegmentStyle(currentIndex, currentLevel, tag, docIndex) {
      const key = `${docIndex}-${tag.value}`
      const hoverState = this.hoverStates[key]
      
      if (hoverState && currentIndex <= hoverState.levelIndex) {
        // When hovering, fill all segments up to hover level with the target color
        return {
          backgroundColor: this.getRatingColorByLevel(hoverState.level),
          border: '1px solid transparent'
        }
      }
      
      // When not hovering, fill segments up to current rating with the current rating's color
      if (tag.name && this.shouldShowFilled(currentIndex, currentLevel, tag, docIndex)) {
        return {
          backgroundColor: this.getRatingColorByLevel(tag.name),
          border: '1px solid transparent'
        }
      }
      
      // Default unfilled styling
      return {}
    },

    getRatingColorByLevel(level) {
      const colors = {
        'Unrated': '#e5e7eb',
        'Basic': '#AAAAAA',
        'Foundational': '#AAAAAA',
        'Intermediate': '#CEDC26',
        'Developing': '#CEDC26',
        'Advanced': '#FFCB00',
        'Proficient': '#50B3CB',
        'Expert': '#50B3CB',
        'Strategic': '#ED742D'
      }
      return colors[level] || '#e5e7eb'
    },

    getFilteredProficiencyLevels() {
      return this.availableProficiencyLevels.filter(level => level !== 'Unrated')
    },

    getDisplayProficiencyText(tag, docIndex) {
      const key = `${docIndex}-${tag.value}`
      const hoverState = this.hoverStates[key]
      
      // If hovering, show the hover level
      if (hoverState) {
        return hoverState.level
      }
      
      // If no proficiency is set, show "Set Proficiency"
      if (!tag.name || tag.name === 'Unrated') {
        return 'Set Proficiency'
      }
      
      // Otherwise show the current proficiency level
      return tag.name
    },

    getSortedSkills(tags) {
      if (!tags) return []
      
      const activeTags = tags.filter(tag => !tag.isDeleted)
      const deletedTags = tags.filter(tag => tag.isDeleted)
      
      // Sort active tags by sequence, then append deleted tags
      const sortedActive = activeTags.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      const sortedDeleted = deletedTags.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      
      return [...sortedActive, ...sortedDeleted]
    },

    handleDrop(event, dropIndex, docIndex) {
      event.preventDefault()
      this.dropIndicatorIndex = null
      
      // Only allow drops within the same document
      if (this.draggedDocIndex !== docIndex) {
        return
      }

      if (this.draggedIndex !== dropIndex) {
        const doc = this.item.documents[docIndex]
        
        // IMPORTANT: Emit the change BEFORE making any modifications to capture original state
        this.$emit('document-skill-update', {
          documentName: doc.jobFamily || doc.name,
          documentReference: doc.reference || '',
          doc_id: doc.doc_id,
          dp_id: doc.dp_id,
          dpa_id: doc.dpa_id
        })
        
        const sortedTags = this.getSortedSkills(doc.tags)
        
        // Remove dragged item and insert at new position - immediate changes
        const newOrder = [...sortedTags]
        const [draggedItem] = newOrder.splice(this.draggedIndex, 1)
        newOrder.splice(dropIndex, 0, draggedItem)
        
        // Update sequence numbers while preserving individual skill identity  
        // Instead of replacing the entire array, update sequences in place
        const originalTags = this.item.documents[docIndex].tags
        
        // First, update all sequences based on new order
        newOrder.forEach((reorderedTag, newIndex) => {
          const originalTag = originalTags.find(t => t.tag_value_id === reorderedTag.tag_value_id)
          if (originalTag) {
            originalTag.sequence = newIndex + 1
          }
        })
        
        // Emit notification for reordering
        this.$emit('notification', {
          text: `Skill priority order updated`,
          type: 'success'
        })
      }
    },

    getUniqueSkillsCount(documents) {
      if (!documents) return 0
      const allSkills = new Set()
      documents.forEach(doc => {
        if (doc.tags) {
          doc.tags.forEach(tag => allSkills.add(tag.value))
        }
      })
      return allSkills.size
    },

    getSharedSkillsCount(documents) {
      if (!documents || documents.length < 2) return 0
      
      const skillSets = documents.map(doc => 
        new Set(doc.tags?.map(tag => tag.value) || [])
      )
      
      return skillSets.reduce((shared, current) => 
        new Set([...shared].filter(skill => current.has(skill)))
      ).size
    },

    getSharedSkills(documents) {
      if (!documents || documents.length < 2) return []
      
      const skillSets = documents.map(doc => 
        new Set(doc.tags?.map(tag => tag.value) || [])
      )
      
      const sharedSkills = skillSets.reduce((shared, current) => 
        new Set([...shared].filter(skill => current.has(skill)))
      )
      
      return Array.from(sharedSkills)
    },

    addSkillToDocument(doc, skillName) {
      // Find the skill tag definition from props.tags or manuallyAddedSkills
      let skillTagDef = this.tags.find(tag => tag.value === skillName)
      // Support manually added skills (from parent, via prop or inject)
      if (!skillTagDef && this.$parent && this.$parent.manuallyAddedSkills) {
        skillTagDef = this.$parent.manuallyAddedSkills.find(tag => tag.value === skillName)
      }
      if (!skillTagDef) {
        this.$emit('notification', {
          text: `Could not find skill definition for: ${skillName}`,
          type: 'error'
        })
        return
      }
      
      // Emit change event BEFORE making modifications to capture original state
      this.$emit('document-skill-update', {
        documentName: doc.jobFamily || doc.name,
        documentReference: doc.reference || '',
        doc_id: doc.doc_id,
        dp_id: doc.dp_id,
        dpa_id: doc.dpa_id,
        tpa_id: doc.tpa_id,
        tp_id: doc.tp_id,
        dp_name: doc.dp_name,
        tag_type_id: doc.tag_type_id
      })
      
      // Get the current highest sequence number
      const currentTags = doc.tags || []
      const maxSequence = currentTags.length > 0 
        ? Math.max(...currentTags.map(tag => tag.sequence || 0))
        : 0
      
      // Create a unique identifier for new skills to track them individually
      const tempTagValueId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      // Create new tag object with proper tag_type_id and unique identifier
      const newTag = {
        tag_value_id: tempTagValueId, // Unique temporary ID for new skills
        value: skillName,
        name: null, // Default to 'Unrated'
        tag_type_rating_id: null,
        sequence: maxSequence + 1,
        classifiers: skillTagDef.classifiers || [], // Copy classifiers from definition
        // Ensure tag_type_id is included - prioritize from document, then from skill definition
        tag_type_id: doc.tag_type_id || skillTagDef.tag_type_id,
        description: skillTagDef.description || '',
        // Mark as new for tracking purposes
        isNewSkill: true,
        doc_id: doc.doc_id // Track which document this belongs to
      }
      
      // Add the new tag to the document's tags array
      if (!doc.tags) {
        this.$set(doc, 'tags', [])
      }
      doc.tags.push(newTag)
      
      // Emit notification for successful addition
      this.$emit('notification', {
        text: `Added skill: ${skillName}`,
        type: 'success'
      })
    },

    // Check if a specific document has pending changes
    hasDocumentPendingChanges(doc) {
      if (!doc) return false
      const documentName = doc.jobFamily || doc.name
      const documentReference = doc.reference || ''
      const docKey = this.getDocumentKey(documentName, documentReference)
      
      return this.hasDocumentPendingChangesByKey(docKey)
    },

    // Helper function to create document key (matching parent component)
    getDocumentKey(documentName, documentReference) {
      return `${documentName}|${documentReference || 'no-ref'}`
    },

    showProficiencyModal(doc, rating, docIndex) {
      this.currentProficiencyLevel = rating
      this.newProficiencyLevel = ''
      this.selectedDocumentIndex = docIndex
      
      // Find all skills with this rating in the document
      this.selectedSkillsForProficiencyChange = doc.tags.filter(tag => {
        const tagRating = tag.name || 'Unrated'
        return tagRating === rating
      })
      
      this.proficiencyModalOpen = true
    },

    // Handle proficiency change including delete
    handleProficiencyChange(doc, tag, newValue, docIndex) {
      if (newValue === 'DELETE_SKILL') {
        this.deleteSkill(doc, tag, docIndex)
      } else {
        this.changeProficiency(doc, tag, newValue, docIndex)
      }
    },

    // Get proficiency items with delete option
    getProficiencyItemsWithDelete(tag) {
      if (tag.isDeleted) {
        // For deleted items, don't show DELETE_SKILL option
        return this.availableProficiencyLevels
      }
      return [...this.availableProficiencyLevels, 'DELETE_SKILL']
    },

    // Delete skill functionality
    deleteSkill(doc, tag, docIndex) {
      // Emit change event BEFORE making modifications
      this.$emit('document-skill-update', {
        documentName: doc.jobFamily || doc.name,
        documentReference: doc.reference || '',
        doc_id: doc.doc_id,
        dp_id: doc.dp_id,
        dpa_id: doc.dpa_id
      })

      // Find the specific tag by its unique identifier
      const targetTag = this.item.documents[docIndex].tags.find(t => 
        t.tag_value_id === tag.tag_value_id && 
        t.value === tag.value
      )

      if (targetTag) {
        // Store the previous name before deletion for possible restoration
        // Only store if not already deleted (avoid overwriting a good value with DELETE_SKILL)
        if (!targetTag.isDeleted) {
          targetTag._previousName = targetTag.name
          // Store the original sequence for undo
          if (targetTag._originalSequence === undefined) {
            targetTag._originalSequence = targetTag.sequence
          }
        }
        // Mark as deleted instead of removing
        targetTag.isDeleted = true
        // Set sequence to null for deleted skills
        targetTag.sequence = null

        // Update sequence numbers for remaining non-deleted skills
        this.reorderSkillsAfterDeletion(docIndex)

        // Emit notification for user feedback
        this.$emit('notification', {
          text: `Skill "${tag.value}" marked for deletion`,
          type: 'warning'
        })
      }
    },

    // Helper to sanitize tags after revert to remove any lingering DELETE_SKILL values
    sanitizeTagsAfterRevert(tags) {
      if (!Array.isArray(tags)) return;
      tags.forEach(tag => {
        if (tag.name === 'DELETE_SKILL') {
          tag.name = null;
          tag.tag_type_rating_id = null;
        }
        // Remove any _previousName cache as well
        if (tag._previousName === 'DELETE_SKILL') {
          tag._previousName = null;
        }
      });
    },

    // Reorder skills after deletion to maintain proper sequence
    reorderSkillsAfterDeletion(docIndex) {
      const allTags = this.item.documents[docIndex].tags
      const activeTags = allTags.filter(tag => !tag.isDeleted)
      const deletedTags = allTags.filter(tag => tag.isDeleted)
      
      // Reorder active tags with consecutive sequence numbers
      activeTags
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .forEach((tag, index) => {
          tag.sequence = index + 1
        })
      
      // Set deleted tags' sequence to null
      deletedTags.forEach(tag => {
        tag.sequence = null
      })
    },

    // Add this lifecycle hook to sanitize tags after revert
    updated() {
      // For each document, sanitize tags to remove lingering DELETE_SKILL
      if (this.item && Array.isArray(this.item.documents)) {
        this.item.documents.forEach(doc => {
          if (Array.isArray(doc.tags)) {
            this.sanitizeTagsAfterRevert(doc.tags);
          }
        });
      }
    },
    // Add a public method to trigger rerender (called by parent on revert)
    triggerRerender() {
      this.rerenderKey++;
    },
    // Helper to check if a skill is focused
    isSkillFocused(skillName) {
      return this.focusedSkills && this.focusedSkills.includes(skillName)
    },

    // Toggle skill focus on click (not drag)
    toggleSkillFocus(skillName) {
      // Prevent focus toggling during drag operations
      if (this.isDragging) {
        return
      }
      // Add a small delay to ensure drag state is fully reset
      setTimeout(() => {
        // Only emit event, parent should handle updating focusedSkills
        this.$emit('toggleSkillFocus', skillName)
      }, 10)
    },

    // Undo delete skill
    undoDeleteSkill(doc, tag, docIndex) {
      // Emit change event BEFORE making modifications
      this.$emit('document-skill-update', {
        documentName: doc.jobFamily || doc.name,
        documentReference: doc.reference || '',
        doc_id: doc.doc_id,
        dp_id: doc.dp_id,
        dpa_id: doc.dpa_id
      })

      // Restore the skill
      tag.isDeleted = false
      // Restore previous proficiency if available, else Unrated
      if (tag._previousName && tag._previousName !== 'DELETE_SKILL' && tag._previousName !== null) {
        tag.name = tag._previousName
        tag.tag_type_rating_id = this.getRatingIdForProficiency(tag._previousName, tag)
      } else {
        tag.name = null
        tag.tag_type_rating_id = null
      }
      delete tag._previousName

      // Restore to its previous position using _originalSequence if available
      this.reorderSkillsAfterRestoration(docIndex, tag)

      // Emit notification for user feedback
      this.$emit('notification', {
        text: `Skill "${tag.value}" restored`,
        type: 'success'
      })
    },
  }
}
</script>

<style scoped>
.space-y-4 > * + * { margin-top: 1rem; }

/* Proficiency Selector Container */
.proficiency-selector-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Rating Segments Container */
.rating-segments-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  width: 100%;
  min-width: 0;
}

/* Rating Segments */
.rating-segments {
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 1px;
  border-radius: 6px;
}

.rating-segment {
  width: 10px;
  height: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #EEEEEE;
  position: relative;
  border-radius: 2px;
  border: 1px solid #DDDDDD;
}

.rating-segment.first {
  border-radius: 8px 2px 2px 8px;
}

.rating-segment.last {
  border-radius: 2px 8px 8px 2px;
}

.rating-segment:only-child {
  border-radius: 8px;
}

/* Filled state colors are now handled dynamically via getSegmentStyle() */

.rating-segment:hover {
  transform: scale(1.1);
  z-index: 2;
}

.rating-segment.active {
  border: 1px solid rgba(0, 0, 0, 0.3);
  z-index: 1;
}

.delete-skill-btn {
  margin-left: 6px;
  padding: 0px 0px;
  height: 23px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-skill-btn:hover {
  background: #fee2e2;
  border-color: #f87171;
  transform: scale(1.1);
}

/* Proficiency Text */
.proficiency-text {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-align: right;
  min-height: 12px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 80px;
}

.proficiency-text.unrated {
  color: #dc2626;
}

.space-y-3 > * + * { margin-top: 0.75rem; }
.space-y-2 > * + * { margin-top: 0.5rem; }
.space-y-1\.5 > * + * { margin-top: 0.375rem; }
.overflow-x-auto { overflow-x: auto; }
.min-w-max { min-width: max-content; }
.w-72 { width: 18rem; }
.flex-shrink-0 { flex-shrink: 0; }
.gap-4 { gap: 1rem; }
.gap-1 { gap: 0.25rem; }
.border-b { border-bottom-width: 1px; }
.border-t { border-top-width: 1px; }
.bg-gray-50 { background-color: #f9fafb; }
.leading-tight { line-height: 1.25; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 0.75rem; }
.ml-2 { margin-left: 0.5rem; }
.mt-6 { margin-top: 1.5rem; }
.mt-3 { margin-top: 0.75rem; }
.pt-3 { padding-top: 0.75rem; }
.dull {
  opacity: 0.3;
}

/* Drag and Drop Ghost Classes */
.skill-dragging {
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  opacity: 0.5 !important;
  transform: rotate(2deg);
  border: 2px dashed #3b82f6;
  background-color: #eff6ff;
}

.drop-zone-active {
  border: 2px dashed #3b82f6;
  background-color: #eff6ff;
  transform: scale(1.02);
}

.drop-indicator {
  position: absolute;
  left: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to bottom, #3b82f6, #1d4ed8);
  border-radius: 2px;
  z-index: 1001;
  box-shadow: 0 0 8px rgba(0, 130, 246, 0.6);
}

.drop-indicator::before {
  content: '';
  position: absolute;
  top: -6px;
  left: -4px;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 130, 246, 0.8);
}

.drop-indicator::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: -4px;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 130, 246, 0.8);
}

/* Skill Focusing and Dulling Effects */
.skill-dulled {
  opacity: 0.3;
  filter: grayscale(50%);
  transition: all 0.2s ease;
}

.skill-dulled:hover {
  opacity: 0.6;
  filter: grayscale(20%);
}

.skill-hidden {
  display: none !important;
}

.skill-focused {
  opacity: 1;
  filter: none;
  background-color: #eff6ff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Pending changes indicator */
.pending-changes-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

/* Additional utility classes */
.text-center { text-align: center; }
.py-8 { padding-top: 2rem; padding-bottom: 2rem; }
.py-6 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
.text-gray-500 { color: #6b7280; }
.text-6xl { font-size: 3.75rem; line-height: 1; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.flex { display: flex; }
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.justify-between { justify-content: space-between; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 400; }
.font-bold { font-weight: 700; }
.text-gray-700 { color: #374151; }
.text-gray-600 { color: #4b5563; }
.text-gray-900 { color: #111827; }
.text-blue-700 { color: #1d4ed8; }
.text-blue-800 { color: #1e40af; }
.text-blue-900 { color: #1e3a8a; }
.text-green-700 { color: #15803d; }
.text-yellow-700 { color: #a16207; }
.border { border-width: 1px; }
.rounded { border-radius: 0.25rem; }
.rounded-lg { border-radius: 0.5rem; }
.overflow-hidden { overflow: hidden; }
.bg-white { background-color: #ffffff; }
.p-3 { padding: 0.75rem; }
.p-2 { padding: 0.5rem; }
.p-4 { padding: 1rem; }
.p-1\.5 { padding: 0.375rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.px-1\.5 { padding-left: 0.375rem; padding-right: 0.375rem; }
.py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.py-0\.5 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
.pr-2 { padding-right: 0.5rem; }
.flex-1 { flex: 1 1 0%; }
.grid { display: grid; }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.flex-wrap { flex-wrap: wrap; }

/* Color classes for lifecycle status */
.bg-green-100 { background-color: #dcfce7; }
.text-green-700 { color: #15803d; }
.bg-yellow-100 { background-color: #fef3c7; }
.text-yellow-700 { color: #a16207; }

/* Blue theme for comparison overview */
.bg-blue-50 { background-color: #eff6ff; }
.border-blue-200 { border-color: #bfdbfe; }
.text-blue-900 { color: #1e3a8a; }
.text-blue-800 { color: #1e40af; }
.text-blue-700 { color: #1d4ed8; }
.bg-blue-100 { background-color: #dbeafe; }
.border-blue-200 { border-color: #bfdbfe; }
.bg-cyan-100 { background-color: #50b3cb; } 
.text-cyan-800 { color: #0b232c; } 
.border-cyan-300 { border-color: #1b9dd1; }

.bg-lime-100 { background-color: #50cb50; } 
.text-lime-800 { color: #0b2c0e; } 
.border-lime-300 { border-color: #099628; }

/* Rating color classes */
.bg-gray-100 { background-color: #f3f4f6; }
.text-gray-800 { color: #1f2937; }
.border-gray-300 { border-color: #d1d5db; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
.border-blue-300 { border-color: #93c5fd; }
.bg-green-100 { background-color: #dcfce7; }
.text-green-800 { color: #166534; }
.border-green-300 { border-color: #86efac; }
.bg-purple-100 { background-color: #f3e8ff; }
.text-purple-800 { color: #6b21a8; }
.border-purple-300 { border-color: #c4b5fd; }
.bg-orange-100 { background-color: #fed7aa; }
.text-orange-800 { color: #9a3412; }
.border-orange-300 { border-color: #fdba74; }

.missing-skill-indicator {
  opacity: 1;
  box-shadow: 0 0 0 1px #fca5a5; /* Reduced from 2px */
  font-size: 13px; /* Reduced from 13px */
  display: inline-flex;
  align-items: center;
  margin: 2px 2px 2px 0px;
  height: 30px !important;
}
.material-icons {
  font-family: 'Material Icons';
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  /* Support for Google Material Icons font */
}

.add-skill-btn {
  flex-shrink: 0;
  width: 16px; /* Reduced from 20px */
  height: 16px; /* Reduced from 20px */
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-skill-btn:focus {
  outline: 2px solid #10b981;
  outline-offset: 1px;
}

.doc-header-ellipsis {
  max-width: 120px; /* Reduced from 140px */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
}

.skills-list {
  display: flex;
  flex-direction: column;
  overflow-y:scroll;
  max-height:calc(100vh - 525px);
}

.skill-item {
  transition: all 0.2s ease;
  border-bottom: 1px solid #e5e7eb;
  cursor: grab;
  width: 100%;
  min-height: 2rem;
  box-sizing: border-box;
  position: relative;
}

.proficiency-selector {
  min-width: 130px;
  max-width: 160px;
  height: 30px;
  max-height: 30px;
}

.proficiency-select-compact {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
}

.proficiency-dropdown-compact {
  font-size: 8px !important;
  height: 30px !important;
  max-height: 30px !important;
}

/* Force all v-input internal elements to 30px height */
.proficiency-dropdown-compact .v-input {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
}

.proficiency-dropdown-compact .v-input__control {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
}

.proficiency-dropdown-compact .v-input__slot {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
  padding: 0 4px !important;
  display: flex !important;
  align-items: center !important;
}

/* Force all selection and text field elements to fit within 30px */
.proficiency-dropdown-compact .v-select__selection {
  margin: 0 !important;
  line-height: 1 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  height: 28px !important;
  max-height: 28px !important;
}

.proficiency-dropdown-compact .v-select__selections {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
  display: flex !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Force input group and field wrapper to 30px */
.proficiency-dropdown-compact .v-input-group {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
}

.proficiency-dropdown-compact .v-text-field {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
}

.proficiency-dropdown-compact .v-text-field__details {
  display: none !important;
}

/* Force the actual input element sizing */
.proficiency-dropdown-compact input {
  height: 28px !important;
  max-height: 28px !important;
  min-height: 28px !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 28px !important;
}

.proficiency-dropdown-compact .v-icon {
  font-size: 14px !important;
}

.proficiency-select-compact .v-select__slot {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
  display: flex !important;
  align-items: center !important;
}

.proficiency-select-compact .v-input__append-inner {
  margin-top: 0 !important;
  padding-left: 2px !important;
  height: 30px !important;
  max-height: 30px !important;
  display: flex !important;
  align-items: center !important;
}

/* Override Vuetify styles for 30px height dropdown - comprehensive targeting */
.proficiency-select-compact .v-input__control .v-input__slot {
  box-shadow: none !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 4px !important;
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
  display: flex !important;
  align-items: center !important;
}

.proficiency-select-compact .v-input--is-focused .v-input__slot {
  border-color: #3b82f6 !important;
}

/* Force wrapper and container elements */
.proficiency-select-compact .v-input__control {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
}

.proficiency-select-compact .v-input {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
}

/* Target any remaining internal elements that might expand height */
.proficiency-select-compact * {
  box-sizing: border-box !important;
}

.proficiency-dropdown-compact .v-input__control > *,
.proficiency-dropdown-compact .v-input__slot > *,
.proficiency-dropdown-compact .v-select__selections > * {
  max-height: 28px !important;
  overflow: hidden !important;
}

/* Vuetify dense outlined text field height fix - comprehensive override */
.v-text-field--filled.v-input--dense.v-text-field--outlined.v-text-field--filled > .v-input__control > .v-input__slot,
.v-text-field--filled.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot,
.v-text-field--filled.v-input--dense.v-text-field--single-line > .v-input__control > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--outlined.v-text-field--filled > .v-input__control > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot,
.v-text-field--full-width.v-input--dense.v-text-field--single-line > .v-input__control > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--outlined.v-text-field--filled > .v-input__control > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--outlined > .v-input__control > .v-input__slot,
.v-text-field--outlined.v-input--dense.v-text-field--single-line > .v-input__control > .v-input__slot {
  min-height: 29px !important;
  max-height: 30px !important;
}

/* Apply the same fix specifically to our proficiency dropdowns */
.proficiency-select-compact.v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot,
.proficiency-dropdown-compact.v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot,
.proficiency-select-compact.v-select--outlined.v-input--dense > .v-input__control > .v-input__slot,
.proficiency-dropdown-compact.v_select--outlined.v-input--dense > .v-input__control > .v-input__slot {
  min-height: 29px !important;
  max-height: 30px !important;
  height: 30px !important;
}

/* Force all v-select dense outlined variants to respect height */
.proficiency-select-compact.v_select.v-input--dense .v-input__slot,
.proficiency-dropdown-compact.v_select.v-input--dense .v-input__slot {
  min-height: 29px !important;
  max-height: 30px !important;
  height: 30px !important;
}

.v-text-field--outlined.v-input--dense.v-text-field--outlined>.v-input__control>.v-input__slot {
  min-height: 29px !important;
  max-height: 30px !important;
  height: 30px !important;
}

.skill-item {
  min-height: 2.25rem;
  align-items: center;
  padding: 0.25rem 0.375rem;
}

.skill-item .flex.items-center.justify-between {
  align-items: center;
  gap: 4px;
  height: 100%;
}

.drag-handle {
  transition: color 0.2s ease;
  cursor: grab;
  border-radius: 3px;
  padding: 2px;
  height: fit-content;
}

.priority-number {
  font-size: 7px;
  line-height: 1;
  max-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
}

/* Nuclear option - target all possible internal Vuetify elements that could expand height */
.proficiency-select-compact .v-input__control,
.proficiency-dropdown-compact .v-input__control,
.proficiency-select-compact .v-input__slot,
.proficiency-dropdown-compact .v-input__slot,
.proficiency-select-compact .v-text-field__slot,
.proficiency-dropdown-compact .v-text-field__slot,
.proficiency-select-compact .v-select__slot,
.proficiency-dropdown-compact .v-select__slot {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
  line-height: 30px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  box-sizing: border-box !important;
}

/* Target the actual div.v-input__control and div.v-input__slot specifically */
.proficiency-select-compact div.v-input__control,
.proficiency-dropdown-compact div.v-input__control,
.proficiency-select-compact div.v-input__slot,
.proficiency-dropdown-compact div.v-input__slot {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
  overflow: hidden !important;
}

/* Force all children of input control and slot to be contained */
.proficiency-select-compact .v-input__control > *,
.proficiency-dropdown-compact .v-input__control > *,
.proficiency-select-compact .v-input__slot > *,
.proficiency-dropdown-compact .v-input__slot > * {
  max-height: 30px !important;
  overflow: hidden !important;
  line-height: 30px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  box-sizing: border-box !important;
}

/* Specific targeting for Vuetify v2 structure */
.proficiency-select-compact.v-text-field.v-text-field--outlined.v.input--dense > .v-input__control > .v-input__slot,
.proficiency-dropdown-compact.v-text-field.v-text-field--outlined.v.input--dense > .v-input__control > .v-input__slot {
  min-height: 30px !important;
  max-height: 30px !important;
  height: 30px !important;
  padding: 0 8px !important;
  display: flex !important;
  align-items: center !important;
  overflow: hidden !important;
}

/* Aggressive override for any remaining height-expanding elements */
.proficiency-select-compact *[class*="v-input"],
.proficiency-dropdown-compact *[class*="v-input"],
.proficiency-select-compact *[class*="v-text-field"],
.proficiency-dropdown-compact *[class*="v-text-field"],
.proficiency-select-compact *[class*="v-select"],
.proficiency-dropdown-compact *[class*="v-select"] {
  max-height: 30px !important;
  overflow: hidden !important;
}

/* Force any div inside the components to respect height */
.proficiency-select-compact div,
.proficiency-dropdown-compact div {
  max-height: 30px !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

/* Ensure the component root elements are properly constrained */
.proficiency-select-compact,
.proficiency-dropdown-compact {
  max-height: 30px !important;
  overflow: hidden !important;
  line-height: 30px !important;
  align-items: center;
  padding-bottom: 2px;
}

/* Final override using attribute selectors to catch any missed elements */
.proficiency-select-compact [class*="input"],
.proficiency-dropdown-compact [class*="input"],
.proficiency-select-compact [class*="control"],
.proficiency-dropdown-compact [class*="control"],
.proficiency-select-compact [class*="slot"],
.proficiency-dropdown-compact [class*="slot"] {
  max-height: 30px !important;
  overflow: hidden !important;
  line-height: 30px !important;
  box-sizing: border-box !important;
}

/* Fix label positioning - force label to always be in "focused" position */
.proficiency-select-compact .v-label,
.proficiency-dropdown-compact .v-label {
  transform: translateY(-18px) scale(0.75) !important;
  transform-origin: top left !important;
  top: 6px !important;
  left: 12px !important;
  color: #757575 !important;
  font-size: 12px !important;
  line-height: 1 !important;
  pointer-events: none !important;
  transition: none !important;
}

/* Ensure focused state maintains the same position */
.proficiency-select-compact.v-input--is-focused .v-label,
.proficiency-dropdown-compact.v-input--is-focused .v-label,
.proficiency-select-compact .v-input--is-focused .v-label,
.proficiency-dropdown-compact .v-input--is-focused .v-label {
  transform: translateY(-18px) scale(0.75) !important;
  transform-origin: top left !important;
  top: 6px !important;
  left: 12px !important;
  color: #1976d2 !important;
  font-size: 12px !important;
  line-height: 1 !important;
}

/* Override any label states that might cause jumping */
.proficiency-select-compact .v-label--active,
.proficiency-dropdown-compact .v-label--active,
.proficiency-select-compact .v-input--has-state .v-label,
.proficiency-dropdown-compact .v-input--has-state .v-label {
  transform: translateY(-18px) scale(0.75) !important;
  transform-origin: top left !important;
  top: 6px !important;
  left: 12px !important;
}

/* Disable label transitions completely to prevent jumping */
.proficiency-select-compact .v-label,
.proficiency-dropdown-compact .v-label,
.proficiency-select-compact .v-input--is-focused .v-label,
.proficiency-dropdown-compact .v-input--is-focused .v-label {
  transition: none !important;
  will-change: auto !important;
}

/* Force consistent label positioning for outlined dense fields */
.proficiency-select-compact.v-text-field--outlined .v-label,
.proficiency-dropdown-compact.v-text-field--outlined .v-label {
  transform: translateY(-18px) scale(0.75) !important;
  transform-origin: top left !important;
  top: 6px !important;
  left: 12px !important;
  transition: none !important;
}

/* Deleted skill styles */
.skill-deleted {
  background-color: #fef2f2 !important;
  border-color: #fca5a5 !important;
  opacity: 0.6;
  text-decoration: line-through;
  color: #dc2626 !important;
  order: 999; /* Push to bottom visually */
}

.skill-deleted .skill-name {
  text-decoration: line-through;
  color: #dc2626 !important;
}

.skill-deleted .priority-number {
  background-color: #fee2e2 !important;
  color: #dc2626 !important;
  border-color: #fca5a5 !important;
}

.skill-deleted .proficiency-select-compact {
  opacity: 0.5;
  pointer-events: none;
}

/* Delete option in dropdown */
.delete-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: #dc2626 !important;
  background-color: #fef2f2;
  border-top: 1px solid #fca5a5;
  margin-top: 4px;
}

.delete-option:hover {
  background-color: #fee2e2 !important;
}

/* Compact Rating Counter Buttons */
.rating-counter-compact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 10px;
}

.rating-counter-compact:hover {
  border-color: #9ca3af;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.compact-text {
  font-size: 10px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

.compact-chevron {
  color: #6b7280;
}

.rating-color-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* Proficiency Select Option Styling */
.proficiency-select-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  width: 100%;
}

.proficiency-select-segments {
  display: flex;
  gap: 1px;
  align-items: center;
}

.proficiency-select-segment {
  width: 12px;
  height: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.proficiency-select-segment.first {
  border-radius: 6px 2px 2px 6px;
}

.proficiency-select-segment.last {
  border-radius: 2px 6px 6px 2px;
}

.proficiency-select-segment.filled {
  border: 1px solid transparent;
}

.proficiency-select-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}
</style>